/**
 * Skeleton.tienda — Presets de carga de la tienda virtual (admin y pública).
 *
 * QUÉ HACE: skeletons de la lista de pedidos, formulario de configuración,
 *   página pública y detalle de producto, construidos sobre `Skeleton`.
 * QUÉ INVOCA: Skeleton (Skeleton.base).
 * QUÉ EXPORTA: SkeletonPedidosList, SkeletonTiendaConfigForm,
 *   SkeletonTiendaPublicPage, SkeletonTiendaProductDetail.
 * QUÉ NO DEBE HACER: no contiene lógica de negocio ni datos reales.
 */

import React from "react";
import { Skeleton } from "./Skeleton.base";

/* ═══════════════════════════════════════════════════════════════
   PRESET: PEDIDOS ADMIN (lista de pedidos de la tienda virtual)
   ═══════════════════════════════════════════════════════════════ */

/**
 * Skeleton para PedidosList: tarjetas de resumen de ventas + buscador + tabla.
 */
export const SkeletonPedidosList: React.FC = () => (
  <div className="space-y-4" aria-hidden="true">
    {/* 4 tarjetas de resumen (Efectivo / Nequi / Daviplata / Tarjeta)
        — grid-cols-2 igual que ResumenVentasCards */}
    <div className="grid grid-cols-2 gap-3">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-xl p-3 space-y-2"
        >
          <Skeleton width="w-16" height="h-3" rounded="md" />
          <Skeleton width="w-24" height="h-6" rounded="md" />
        </div>
      ))}
    </div>
    {/* Tarjeta Total (row completo) */}
    <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-xl p-3 flex items-center justify-between">
      <Skeleton width="w-12" height="h-3" rounded="md" />
      <Skeleton width="w-28" height="h-6" rounded="md" />
    </div>

    {/* Buscador + toggle vista */}
    <div className="flex gap-2">
      <Skeleton height="h-10" rounded="xl" className="flex-1" />
      <Skeleton width="w-10" height="h-10" rounded="lg" />
      <Skeleton width="w-10" height="h-10" rounded="lg" />
    </div>

    {/* Record count */}
    <Skeleton width="w-44" height="h-3" rounded="md" />

    {/* Tabla */}
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
      {/* Cabecera */}
      <div className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700 px-4 py-3 flex gap-4">
        {[16, 28, 44, 16, 20, 24, 28, 16].map((w, i) => (
          <Skeleton
            key={i}
            width={`w-${w}`}
            height="h-2.5"
            rounded="md"
            className="flex-shrink-0"
          />
        ))}
      </div>
      {/* Filas */}
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="px-4 py-3.5 flex items-center gap-4 border-b border-gray-100 dark:border-slate-700 last:border-0"
        >
          {/* # */}
          <Skeleton
            width="w-8"
            height="h-3.5"
            rounded="md"
            className="flex-shrink-0"
          />
          {/* Estado badge */}
          <Skeleton
            width="w-24"
            height="h-6"
            rounded="full"
            className="flex-shrink-0"
          />
          {/* Cliente: nombre + teléfono */}
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <Skeleton width="w-36" height="h-3" rounded="md" />
            <Skeleton width="w-24" height="h-2.5" rounded="md" />
          </div>
          {/* Productos */}
          <Skeleton
            width="w-12"
            height="h-3"
            rounded="md"
            className="flex-shrink-0"
          />
          {/* Método */}
          <Skeleton
            width="w-16"
            height="h-3"
            rounded="md"
            className="flex-shrink-0"
          />
          {/* Total */}
          <Skeleton
            width="w-20"
            height="h-3.5"
            rounded="md"
            className="flex-shrink-0"
          />
          {/* Fecha */}
          <div className="flex flex-col gap-1 flex-shrink-0">
            <Skeleton width="w-24" height="h-2.5" rounded="md" />
            <Skeleton width="w-16" height="h-2.5" rounded="md" />
          </div>
          {/* Acciones */}
          <div className="flex gap-1.5 flex-shrink-0">
            <Skeleton width="w-7" height="h-7" rounded="lg" />
            <Skeleton width="w-7" height="h-7" rounded="lg" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   PRESET: TIENDA CONFIG FORM (formulario de configuración admin)
   ═══════════════════════════════════════════════════════════════ */

/**
 * Skeleton para TiendaConfigForm: banner de tienda + secciones de formulario
 * + panel de vista previa en desktop.
 * Coincide con el layout lg:grid lg:grid-cols-12.
 */
export const SkeletonTiendaConfigForm: React.FC = () => (
  <div className="lg:grid lg:grid-cols-12 lg:gap-8" aria-hidden="true">
    {/* ── Columna izquierda: formulario ─────────────────────────────── */}
    <div className="lg:col-span-7 xl:col-span-8 space-y-6">
      {/* Banner azul de la tienda */}
      <div className="skeleton rounded-xl" style={{ height: 110 }} />

      {/* Sección: Información del negocio */}
      <div className="space-y-3">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-gray-100 dark:border-slate-700">
          <Skeleton width="w-3" height="h-3" rounded="md" />
          <Skeleton width="w-40" height="h-2.5" rounded="md" />
        </div>
        <Skeleton height="h-8" rounded="lg" />
        <Skeleton height="h-14" rounded="lg" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Skeleton height="h-8" rounded="lg" />
          <Skeleton height="h-8" rounded="lg" />
          <Skeleton height="h-8" rounded="lg" />
        </div>
      </div>

      {/* Sección: Redes sociales */}
      <div className="space-y-3">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-gray-100 dark:border-slate-700">
          <Skeleton width="w-3" height="h-3" rounded="md" />
          <Skeleton width="w-28" height="h-2.5" rounded="md" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Skeleton height="h-8" rounded="lg" />
          <Skeleton height="h-8" rounded="lg" />
          <Skeleton height="h-8" rounded="lg" />
        </div>
      </div>

      {/* Sección: Apariencia y diseño */}
      <div className="space-y-3">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-gray-100 dark:border-slate-700">
          <Skeleton width="w-3" height="h-3" rounded="md" />
          <Skeleton width="w-36" height="h-2.5" rounded="md" />
        </div>
        {/* Logo: circle preview + botón */}
        <div className="flex items-center gap-4">
          <Skeleton width="w-16" height="h-16" rounded="full" />
          <Skeleton width="w-28" height="h-8" rounded="lg" />
        </div>
        {/* Colores: 2 swatches */}
        <div className="grid grid-cols-2 gap-3">
          <Skeleton height="h-9" rounded="lg" />
          <Skeleton height="h-9" rounded="lg" />
        </div>
        {/* Estilo header: chips */}
        <div className="flex flex-wrap gap-2">
          {[56, 72, 64, 80, 60, 68, 76].map((w, i) => (
            <div key={i} className="flex-shrink-0" style={{ width: w }}>
              <Skeleton height="h-8" rounded="full" />
            </div>
          ))}
        </div>
        {/* Icono principal */}
        <Skeleton height="h-9" rounded="lg" />
      </div>

      {/* Botón guardar */}
      <Skeleton height="h-10" rounded="xl" width="w-36" />
    </div>

    {/* ── Columna derecha: vista previa (solo desktop) ───────────────── */}
    <div className="hidden lg:block lg:col-span-5 xl:col-span-4">
      <div className="sticky top-4 space-y-3">
        <Skeleton width="w-24" height="h-3" rounded="md" />
        {/* Header preview */}
        <div className="skeleton rounded-xl" style={{ height: 80 }} />
        {/* Footer preview */}
        <div className="skeleton rounded-xl" style={{ height: 140 }} />
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   PRESET: TIENDA PÚBLICA (página completa de la tienda virtual)
   ═══════════════════════════════════════════════════════════════ */

/**
 * Pantalla completa de carga para la tienda virtual pública.
 * Replica fielmente la estructura de TiendaHeader + TiendaProductGrid +
 * TiendaProductCard + TiendaFooter.
 */
export const SkeletonTiendaPublicPage: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex flex-col" aria-hidden="true">
    {/* ── Header ──────────────────────────────────────────────────────────
        Replica: logo circle (w-16 h-16) + business name (2 líneas)
        + línea decorativa. El contenedor usa bg-gray-200 para que los
        ítems skeleton (también gray-200 con shimmer) sean visibles por
        sus diferencias de brillo.
    ──────────────────────────────────────────────────────────────────── */}
    <div className="w-full flex-shrink-0 bg-gray-200">
      <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 flex flex-row items-center gap-4">
        {/* Logo: w-16 h-16 rounded-full */}
        <Skeleton
          width="w-16"
          height="h-16"
          rounded="full"
          className="flex-shrink-0"
        />
        {/* Nombre + línea decorativa */}
        <div className="flex-1 min-w-0 space-y-2">
          <Skeleton width="w-4/5" height="h-6" rounded="full" />
          <Skeleton width="w-3/5" height="h-6" rounded="full" />
          <Skeleton width="w-12" height="h-1" rounded="full" />
        </div>
      </div>
    </div>

    {/* ── Contenido principal ─────────────────────────────────────────── */}
    <div className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-4">
      {/* Buscador: rounded-2xl py-3 h-11 */}
      <Skeleton height="h-11" rounded="xl" />

      {/* Chips de categoría: "Todos" + 1ª categoría + "+N más" (exactamente 3) */}
      <div className="flex items-center gap-2">
        <Skeleton width="w-16" height="h-8" rounded="full" />
        <Skeleton width="w-16" height="h-8" rounded="full" />
        <Skeleton width="w-20" height="h-8" rounded="full" />
      </div>

      {/* Grid — MISMAS columnas que TiendaProductGrid: 2 / sm:3 / md:4 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {/* 8 items = 2 filas completas en desktop (4 cols) */}
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm"
          >
            {/* Imagen: w-full aspect-square con botón + flotante (bottom-1.5 right-1.5) */}
            <div className="relative w-full aspect-square overflow-hidden">
              <div className="skeleton absolute inset-0" />
              {/* Botón + — shimmer también para coherencia visual */}
              <div className="skeleton absolute bottom-1.5 right-1.5 w-7 h-7 rounded-full" />
            </div>

            {/* Info: p-2.5 pb-3 — replica exactamente TiendaProductCard */}
            <div className="p-2.5 pb-3">
              {/* Categoría: text-[10px] uppercase mb-0.5 */}
              <Skeleton
                width="w-12"
                height="h-2.5"
                rounded="full"
                className="mb-0.5"
              />
              {/* Nombre: text-xs leading-tight line-clamp-2 min-h-[2rem]
                  2 líneas × h-3 (12px) + gap-1 (4px) = 28px ≈ 2rem */}
              <div className="space-y-1 min-h-[2rem]">
                <Skeleton width="w-full" height="h-3" rounded="md" />
                <Skeleton width="w-3/4" height="h-3" rounded="md" />
              </div>
              {/* Precio: mt-1.5 text-sm */}
              <div className="mt-1.5">
                <Skeleton width="w-16" height="h-[14px]" rounded="md" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* ── Footer ──────────────────────────────────────────────────────────
        Replica: colorFooter (#0f172a) + grid 2 col:
          Col1 — logo w-14 h-14 + nombre + descripción + 3 iconos redes
          Col2 — "Contacto" + 3 filas (icono w-8 h-8 + texto)
        + barra inferior Altenot (gestionado con / ALTENOT / subtítulo)
    ──────────────────────────────────────────────────────────────────── */}
    <footer
      className="w-full flex-shrink-0 border-t border-white/10"
      style={{ backgroundColor: "#0f172a" }}
    >
      <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-8 space-y-6">
        {/* Columna 1 — Marca */}
        <div className="space-y-4">
          {/* Logo + nombre */}
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full flex-shrink-0 bg-white/10" />
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="h-4 rounded-md bg-white/20 w-3/4" />
              <div className="h-3 rounded-md bg-white/10 w-1/2" />
            </div>
          </div>
          {/* Descripción: 2 líneas */}
          <div className="space-y-1.5">
            <div className="h-2.5 rounded bg-white/10 w-full" />
            <div className="h-2.5 rounded bg-white/10 w-5/6" />
          </div>
          {/* Redes sociales: 3 círculos w-10 h-10 */}
          <div className="flex gap-2 pt-1">
            {[0, 1, 2].map((j) => (
              <div key={j} className="w-10 h-10 rounded-full bg-white/10" />
            ))}
          </div>
        </div>

        {/* Columna 2 — Contacto */}
        <div className="space-y-4">
          {/* Label "Contacto" */}
          <div className="h-2 rounded bg-white/10 w-14" />
          {/* 3 filas: icono rounded-lg + línea de texto */}
          {[0, 1, 2].map((j) => (
            <div key={j} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex-shrink-0" />
              <div className="h-3 rounded bg-white/10 flex-1" />
            </div>
          ))}
        </div>
      </div>

      {/* Barra inferior — Altenot branding */}
      <div className="border-t border-black/20 bg-black/20 py-4 flex flex-col items-center gap-1.5">
        <div className="h-2 rounded bg-white/10 w-20" />
        <div className="h-4 rounded bg-white/20 w-24" />
        <div className="h-2 rounded bg-white/10 w-44" />
      </div>
    </footer>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   PRESET: TIENDA — DETALLE DE PRODUCTO
   Replica: TiendaProductDetail (botón volver, grid imagen+info,
   ficha, botones, sección "Explora más" con carrusel)
   ═══════════════════════════════════════════════════════════════ */
export const SkeletonTiendaProductDetail: React.FC = () => (
  <div className="space-y-8" aria-hidden="true">
    {/* Botón volver */}
    <Skeleton width="w-36" height="h-5" rounded="full" />

    {/* Grid: imagen + info */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-start">
      {/* Imagen principal + miniaturas */}
      <div className="space-y-3 md:sticky md:top-4 md:self-start">
        <div className="skeleton w-full aspect-square rounded-2xl" />
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="skeleton w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex-shrink-0"
            />
          ))}
        </div>
      </div>

      {/* Info del producto */}
      <div className="flex flex-col gap-4">
        {/* Badge categoría */}
        <Skeleton width="w-24" height="h-6" rounded="full" />
        {/* Nombre */}
        <div className="space-y-2">
          <Skeleton width="w-full" height="h-8" rounded="md" />
          <Skeleton width="w-3/5" height="h-8" rounded="md" />
        </div>
        {/* Precio */}
        <Skeleton width="w-32" height="h-9" rounded="md" />
        {/* Stock */}
        <div className="flex items-center gap-2">
          <div className="skeleton w-2.5 h-2.5 rounded-full" />
          <Skeleton width="w-44" height="h-4" rounded="full" />
        </div>
        {/* Separador */}
        <div className="skeleton h-px w-full" />
        {/* Descripción (titular + líneas) */}
        <div className="space-y-2">
          <Skeleton width="w-44" height="h-4" rounded="md" />
          <Skeleton width="w-full" height="h-3" rounded="md" />
          <Skeleton width="w-full" height="h-3" rounded="md" />
          <Skeleton width="w-3/4" height="h-3" rounded="md" />
        </div>
        {/* Ficha del producto (3 filas de característica) */}
        <div className="space-y-2">
          <Skeleton width="w-36" height="h-4" rounded="md" />
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex items-center gap-3 py-2 border-b border-gray-100"
            >
              <div className="skeleton w-8 h-8 rounded-lg flex-shrink-0" />
              <Skeleton width="w-24" height="h-3" rounded="md" />
              <Skeleton
                width="w-20"
                height="h-3"
                rounded="md"
                className="ml-auto"
              />
            </div>
          ))}
        </div>
        {/* Separador */}
        <div className="skeleton h-px w-full" />
        {/* Botones: agregar + compartir */}
        <div className="space-y-3">
          <Skeleton width="w-full" height="h-12" rounded="xl" />
          <Skeleton width="w-full" height="h-11" rounded="xl" />
        </div>
      </div>
    </div>

    {/* Sección "Explora más productos" */}
    <div className="border-t border-gray-200 pt-6 space-y-4">
      <Skeleton width="w-48" height="h-6" rounded="md" />
      {/* Buscador */}
      <Skeleton width="w-full" height="h-11" rounded="xl" />
      {/* Chips de categoría */}
      <div className="flex items-center gap-2">
        <Skeleton width="w-16" height="h-8" rounded="full" />
        <Skeleton width="w-16" height="h-8" rounded="full" />
        <Skeleton width="w-20" height="h-8" rounded="full" />
      </div>
      {/* Carrusel de cards */}
      <div className="flex gap-3 overflow-hidden">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="flex-shrink-0 w-44 bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm"
          >
            <div className="skeleton w-full aspect-square" />
            <div className="p-2.5 pb-3 space-y-1.5">
              <Skeleton width="w-10" height="h-2.5" rounded="full" />
              <Skeleton width="w-full" height="h-3" rounded="md" />
              <Skeleton width="w-3/4" height="h-3" rounded="md" />
              <Skeleton
                width="w-16"
                height="h-3"
                rounded="md"
                className="mt-1"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
